import { forkJoin } from 'rxjs';
import { concatMap, first, map, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  constructor(
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer,
  ) { }

  getAllUserRoles() {
    return this.httpClient.get(environment.applySubstitutions(environment.api_url) + 'api/UserRole/');
  }

  addUserRole(userUUID, roleUUID) {
    return this.httpClient.put(environment.applySubstitutions(environment.api_url) + 'api/User/' + userUUID + '/Role/' + roleUUID, {});
  }

  removeUserRole(userUUID, roleUUID) {
    return this.httpClient.delete(environment.applySubstitutions(environment.api_url) + 'api/User/' + userUUID + '/Role/' + roleUUID);
  }

  /**
   * Get the profile picture for the user from server.
   *
   * @param  {} userUUID - id of the user
   */
  getUserImage(userUUID) {
    return this.httpClient.get(environment.applySubstitutions(environment.api_url) + 'api/User/' + userUUID + '/image', {
      responseType: 'blob'
    }).pipe(
      map(data => {
        return this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(data));
      })
    );
  }

  saveUserProfile(userData, existingUser = false) {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/User/' + userData.uuid;

    const obs = {};
    if (existingUser) {
      obs['user'] = this.httpClient.put(apiEndpoint, userData);
    } else {
      obs['user'] = this.httpClient.post(
        environment.applySubstitutions(environment.api_url) + 'api/Auth/adminCreateUser',
        this.createUser(userData)
      );
    }

    return obs['user'].pipe(
      tap(async (x: any) => {
        const userUUID = existingUser ? userData.uuid : x.user_uuid;
        if (userData.profilePicture) {
          const formData: FormData = new FormData();
          formData.append('file', userData.profilePicture.requestData);
          this.httpClient.post(
            environment.applySubstitutions(environment.api_url) + 'api/User/' + userUUID + '/image', formData
          ).pipe(first()).subscribe();
        }
      }),
      concatMap((x: any) => {
        const userUUID = existingUser ? userData.uuid : x.user_uuid;
        return this.syncRoles(userUUID, userData).pipe(first());
      })
    );
  }

  syncRoles(userUUID, userData) {
    const obs = [];
    if (userData.newRoles.length === 0 && userData.roles.lenght > 0) {
      // Remove all original roles
      for (const role of userData.roles) {
        obs.push(this.removeUserRole(userUUID, role.uuid).pipe(first()));
      }
    }

    // Look for new roles NOT in originalRoles
    for (const role of userData.newRoles) {
      let found = false;
      if (userData.roles && userData.roles.length > 0) {
        found = userData.roles.some(el => el === role.uuid);
      }
      if (!found) {
        obs.push(this.addUserRole(userUUID, role.uuid).pipe(first()));
      }
    }

    // Look for old roles NOT in newRoles
    if (userData.roles && userData.roles.length > 0) {
      for (const role of userData.roles) {
        const found = userData.newRoles.some(el => el.uuid === role.uuid);
        if (!found) {
          obs.push(this.removeUserRole(userUUID, role.uuid).pipe(first()));
        }
      }
    }

    return forkJoin(obs);
  }

  deleteUser(userData) {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/User/' + userData.uuid;
    return this.httpClient.delete(apiEndpoint);
  }

  createUser(userData) {
    const user = userData;
    user.password = "";
    return user;
  }

  getAllPermissions() {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/UserRolePermission/';
    return this.httpClient.get(apiEndpoint);
  }

  createPermission(payload: { name: string, description: string }) {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/UserRolePermission/';
    return this.httpClient.post(apiEndpoint, payload);
  }

  updatePermission(id: string, payload: { name: string, description: string }) {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/UserRolePermission/' + id;
    return this.httpClient.put(apiEndpoint, payload);
  }

  deletePermission(id: string) {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/UserRolePermission/' + id;
    return this.httpClient.delete(apiEndpoint);
  }

  getAllRoles() {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/UserRole/';
    return this.httpClient.get(apiEndpoint);
  }

  getUnapprovedUsers(queryParams: string = '') {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/User/userAccountRequests' + (queryParams ? queryParams : '');
    return this.httpClient.get(apiEndpoint).pipe(map(res => {
      return {...res, users: res['users'].map(user => ({
        ...user,
        registration_timestamp: new Date(user.timestamp).toISOString()
      }))};
    }));
  }

  approveAccountRequest(uuid: string) {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + `api/User/userAccountRequests/${uuid}`;
    return this.httpClient.put(apiEndpoint, {}).pipe(tap(res => {
      console.log(res);
    }));
  }

  denyAccountRequest(uuid: string) {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + `api/User/userAccountRequests/${uuid}`;
    return this.httpClient.delete(apiEndpoint).pipe(tap(res => {
      console.log(res);
    }));
  }

  createRole(payload: { name: string, description: string, is_admin: boolean, permissions: [] }) {
    const { name, description, permissions } = payload;
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/UserRole/';
    return this.httpClient.post(apiEndpoint, {name, description, is_admin: false, permission_uuids: permissions });
  }

  updateRole(id: string, payload: { name: string, description: string, is_admin: boolean, permissions: [] }) {
    const { name, description, permissions } = payload;

    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/UserRole/' + id;
    return this.httpClient.put(apiEndpoint, {name, description, is_admin: false, permission_uuids: permissions });
  }

  deleteRole(id: string) {
    const apiEndpoint = environment.applySubstitutions(environment.api_url) + 'api/UserRole/' + id;
    return this.httpClient.delete(apiEndpoint);
  }
}
